import type { ProviderData } from "@/classes/Self-Serve/SelfServeDataGenerator";
import type { BusinessValues } from "@/utils/businessLeads";
import type {
  Flow,
  InsuranceValue,
  OperationalMode,
  PaymentType,
  QuizInstanceData,
  SelfServeInfoData,
} from "shared/types";

export interface Slot {
  date: string;
  user_id: string;
  appointment_id: string | null;
  length: number;
  is_fully_booked: boolean;
  is_artificial?: boolean;
}

export type NutritionArea = {
  value: string;
  label: string;
  synonyms?: string[];
};

export type Option = {
  value: string;
  label: string;
};

export type SlotSelection = {
  provider_id: string;
  date_time: string;
  is_artificial?: boolean;
};

export type HourSelectionHandler = {
  value: string;
  key: number | null;
  rde_id: string;
  is_artificial?: boolean;
};

export type Selection =
  | {
      key?: number | string;
      value: string;
      label?: string;
    }
  | {
      key?: string;
      label?: string;
      value: PaymentType;
    }
  | {
      key?: string;
      label?: string;
      value: InsuranceValue;
    };
export interface SlotError {
  message: string;
  //any other value
}

export interface HealthieReturn {
  availableSlotsForRange: Slot[];
  error?: SlotError;
}

export enum Direction {
  Left = "left",
  Right = "right",
  Init = "init",
}

export type Result = {
  communication_style: Selection;
  nutrition_area: Selection;
  state: Selection;
  day: Selection;
  date_time: string;
  provider_id: string;
  payment_method: Selection;
  insurance_provider?: Selection;
  insurance_provider_other?: string;
  lead_name: string;
  lead_phone_number: string;
  lead_email: string;
  timezone: string;
  is_artificial?: boolean;
  diabetes_insulin_pump?: string;
  business_name: BusinessValues | null;
};

export interface AppInput {
  flow: Flow;
  provider_id?: string;
  provider_ids?: string[];
  plan_id?: string;
  available_states?: string[] | undefined;
  available_expertise_areas?: string[] | undefined;
  internal?: {
    manual_rematch?: boolean;
    op_mode?: OperationalMode;
  };
}

export type AppParams = {
  state: string;
  nutrition_area?: string;
  expertise?: string;
  provider_id?: string;
  provider_ids?: string[];
  plan_id?: string;
  communication_style?: string;
  flow?: Flow;
  insurance_provider?: string;
  insurance_provider_other?: string;
  lead_name?: string;
  lead_phone_number?: string;
  lead_email?: string;
  payment_method: PaymentType;
  diabetes_insulin_pump?: string;
  using_rematch_tool?: boolean;
  op_mode?: OperationalMode;
};

export type ExtendedAppParams = AppParams & {
  used_only_url_params: boolean;
  used_only_quiz_params: boolean;
};

export interface AppContext extends AppInput {
  result: Result;
  error_message: string | undefined;
  app_loading_element: HTMLElement | null;
  slots: SlotExtended[] | undefined;
  state_options: Option[];
  nutrition_area_options: Option[];
  payment_method_options: Option[];
  insurance_provider_options: Option[];
  step: string;
  next_allowed: {
    insurance_selection_other: boolean;
    lead_name: boolean;
    lead_phone_number: boolean;
    lead_email: boolean;
  };
  day_slots: DaySlot[];
  rde_slots: RDESlot[];
  provider_data: ProviderData[] | undefined;
  provider_data_raw: ProviderData[] | undefined;
  wanted_expertise: string[];
  pulled_provider_ids: string[];
  applied_slot_filters: boolean;
  internal_completion: {
    used_only_url_params: boolean;
    used_only_quiz_params: boolean;
  };
  hide_back_button: {
    day_selector: boolean;
  };
  prior_data: {
    quiz_instance: QuizInstanceData | null;
    self_serve: SelfServeInfoData | null;
  };
  internal?: {
    manual_rematch?: boolean;
    op_mode?: OperationalMode;
  };
}

export type AppEvents =
  | { type: "end" }
  | { type: "back" }
  | { type: "next" }
  | { type: "start" }
  | { type: "retry" }
  | { type: "finish" }
  | { type: "select"; selection: Selection }
  | { type: "selectSlot"; selection: SlotSelection }
  | { type: "assignRDEData"; rdeData: ProviderData[] }
  | { type: "type"; input: string }
  | { type: "xstate.done.actor.apptActor"; output: HealthieReturn }
  | { type: "xstate.error.actor.apptActor"; error: Error }
  | { type: "xstate.done.actor.getRDEActor"; output: SelfServeInfoData; actorId: string }
  | { type: "xstate.error.actor.getRDEActor"; error: Error; actorId: string };

export type Day = {
  value: string;
  dayOfWeek: string;
  dayOfMonth: string;
  dayApptCount: number;
  dayRdeCount: number; // Added rdeCount
};

export type SlotExtended = Slot & {
  short_date: string;
};

export interface HourGroup {
  hour: string;
  slot: SlotExtended;
}

interface BaseSlot {
  hours: HourGroup[];
  slotCount: number;
}

export interface DayInRDE extends BaseSlot {
  day: string;
  dayOfWeek: string;
  dayOfMonth: string;
}

export interface RDESlot {
  rde: string;
  days: DayInRDE[];
  dayCount: number;
  slotCount: number;
}

export interface RDEInDay extends BaseSlot {
  rde: string;
}

export interface DaySlot {
  day: string;
  dayOfWeek: string;
  dayOfMonth: string;
  rdes: RDEInDay[];
  rdeCount: number;
  slotCount: number;
}
